import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import {
  VendorLifecycleMode,
  VendorOffboardDialogComponent,
  VendorOffboardDialogData,
} from 'src/app/shared/vendor-offboard-dialog/vendor-offboard-dialog.component';

@Component({
  selector: 'app-vendor-accounts-list',
  templateUrl: './vendor-accounts-list.component.html',
  styleUrls: ['./vendor-accounts-list.component.css'],
})
export class VendorAccountsListComponent implements OnInit {
  rows: any[] = [];
  loading = false;
  actionVendorId: number | null = null;

  constructor(
    private readonly api: ApiService,
    private readonly toastr: ToastrMsgService,
    private readonly router: Router,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.api.listVendorAccounts().subscribe({
      next: (res: any) => {
        this.rows = res?.data || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err?.error?.message || 'Failed to load vendors');
      },
    });
  }

  goAdd(): void {
    this.router.navigate(['/home/vendor/add']);
  }

  companyId(row: any): number | null {
    const id = Number(row?.id);
    return Number.isFinite(id) ? id : null;
  }

  loginId(row: any): number | null {
    const id = Number(row?.user_id);
    return Number.isFinite(id) ? id : null;
  }

  isOffboarded(row: any): boolean {
    return !!row.offboarded_on || row.user_is_active === false || row.availability_status === 'offboarded';
  }

  isSuspended(row: any): boolean {
    return !this.isOffboarded(row) && row.availability_status === 'suspended';
  }

  getStatusLabel(row: any): string {
    if (this.isOffboarded(row)) return 'Offboarded';
    if (this.isSuspended(row)) return 'Suspended';
    return row.availability_status || 'available';
  }

  getStatusClass(row: any): string {
    if (this.isOffboarded(row)) return 'bg-red-100 text-red-800';
    if (this.isSuspended(row)) return 'bg-orange-100 text-orange-800';
    if (row.availability_status === 'unavailable') return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  }

  offboardVendor(row: any): void {
    const vendorId = this.loginId(row) || this.companyId(row);
    if (!vendorId) {
      this.toastr.error('No linked vendor login account found');
      return;
    }
    this.openLifecycleDialog(row, 'offboard', (result) => {
      this.actionVendorId = vendorId;
      this.api.offboardVendor(vendorId, { reason: result.reason, force: result.force }).subscribe({
        next: (res: any) => {
          this.toastr.success(res.message || 'Vendor offboarded successfully');
          this.actionVendorId = null;
          this.load();
        },
        error: (err) => {
          this.actionVendorId = null;
          const body = err?.error;
          if (body?.error === 'OPEN_INVOICES') {
            this.toastr.error(
              `${body.message} (${body.openInvoicesCount} open, ₹${body.openInvoicesTotal || 0}). Enable force offboard to override.`
            );
          } else {
            this.toastr.error(body?.message || body?.error || 'Failed to offboard vendor');
          }
        },
      });
    });
  }

  reinstateVendor(row: any): void {
    const vendorId = this.loginId(row) || this.companyId(row);
    if (!vendorId) {
      this.toastr.error('No linked vendor login account found');
      return;
    }
    this.openLifecycleDialog(row, 'reinstate', (result) => {
      this.actionVendorId = vendorId;
      this.api.reinstateVendor(vendorId, { reason: result.reason }).subscribe({
        next: (res: any) => {
          this.toastr.success(res.message || 'Vendor reinstated successfully');
          this.actionVendorId = null;
          this.load();
        },
        error: (err) => {
          this.actionVendorId = null;
          this.toastr.error(err?.error?.message || 'Failed to reinstate vendor');
        },
      });
    });
  }

  suspendVendor(row: any): void {
    const vendorId = this.companyId(row);
    if (!vendorId) {
      this.toastr.error('Vendor company id missing');
      return;
    }
    this.openLifecycleDialog(row, 'suspend', (result) => {
      this.actionVendorId = vendorId;
      this.api.suspendVendor(vendorId, { reason: result.reason }).subscribe({
        next: (res: any) => {
          this.toastr.success(res.message || 'Vendor suspended (login remains active)');
          this.actionVendorId = null;
          this.load();
        },
        error: (err) => {
          this.actionVendorId = null;
          this.toastr.error(err?.error?.message || 'Failed to suspend vendor');
        },
      });
    });
  }

  unsuspendVendor(row: any): void {
    const vendorId = this.companyId(row);
    if (!vendorId) {
      this.toastr.error('Vendor company id missing');
      return;
    }
    this.openLifecycleDialog(row, 'unsuspend', () => {
      this.actionVendorId = vendorId;
      this.api.unsuspendVendor(vendorId).subscribe({
        next: (res: any) => {
          this.toastr.success(res.message || 'Vendor availability restored');
          this.actionVendorId = null;
          this.load();
        },
        error: (err) => {
          this.actionVendorId = null;
          this.toastr.error(err?.error?.message || 'Failed to restore vendor');
        },
      });
    });
  }

  isActionPending(row: any): boolean {
    const ids = [this.companyId(row), this.loginId(row)].filter((id) => id != null);
    return ids.includes(this.actionVendorId);
  }

  private openLifecycleDialog(
    row: any,
    mode: VendorLifecycleMode,
    onConfirm: (result: { reason?: string; force?: boolean }) => void
  ): void {
    const dialogRef = this.dialog.open(VendorOffboardDialogComponent, {
      width: '480px',
      data: {
        vendorName: row.company_name,
        mode,
      } as VendorOffboardDialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      onConfirm(result);
    });
  }
}
